import React from 'react';
import styled from 'styled-components';
import { useAppMetadata } from '../contexts/AppMetadataContext';
import { useI18n } from '../locales/useI18n';
import { breakpoints, colors, typography } from '../styles/theme';
import {
  formatCompactCount,
  getUpcomingEventCounts,
  isAppMetadataFresh,
} from '../utils/appMetadata';

const StatusLine = styled.span`
  display: flex;
  align-items: center;
  gap: 5px;
  min-height: 14px;
  color: ${colors.text.tertiary};
  font-size: 11px;
  font-weight: ${typography.fontWeights.medium};
  line-height: 1.2;
  letter-spacing: 0;
  white-space: nowrap;

  @media (max-width: ${breakpoints.sm}) {
    max-width: 145px;
    overflow: hidden;
    font-size: 10px;
    text-overflow: ellipsis;
  }
`;

const StatusDot = styled.span.withConfig({
  shouldForwardProp: (prop) => prop !== '$fresh',
})<{ $fresh: boolean }>`
  width: 6px;
  height: 6px;
  flex: 0 0 6px;
  border-radius: 50%;
  background: ${(props) => (props.$fresh ? colors.success[500] : colors.gray[400])};
`;

export const HeaderActivityStatus: React.FC = () => {
  const { metadata } = useAppMetadata();
  const { locale, t } = useI18n();
  if (!metadata) return null;

  const counts = getUpcomingEventCounts(metadata);
  const builtAt = new Date(metadata.builtAt);
  const refreshedAt = Number.isNaN(builtAt.getTime())
    ? null
    : new Intl.DateTimeFormat(locale, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(builtAt);
  const summary = `${formatCompactCount(counts.total, locale)} ${t('header.upcomingShort')}`;
  const freshness = refreshedAt
    ? `${t('homepage.liveStats.updated')} ${refreshedAt}`
    : undefined;

  return (
    <StatusLine aria-label={freshness ? `${summary}. ${freshness}` : summary} title={freshness}>
      <StatusDot $fresh={isAppMetadataFresh(metadata.builtAt)} aria-hidden="true" />
      {summary}
    </StatusLine>
  );
};

export default HeaderActivityStatus;